import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { User } from '../../../user/user.model';
import { ScorecardGridOptions } from '../../core/scorecard-grid-options.model';
import {
    GridType,
    TemplateMode,
    Widget,
} from '../../core/template-definition.model';
import {
    bindWidthResize,
    Column,
    Context,
    DivRendererOptions,
    ExperimentalNgGridComponent,
    flattenArrayWithObj,
    GridContent,
    SelectTemplateFn,
    SortOrder,
} from '../../experimental.grid';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { CMPS_SCORECARD_WIDGET } from '../../scorecard.metadata';

import {
    addColumnsMeasure,
    addRowsMeasure,
    autoSizeGrid,
} from './auto-size-grid';
import {
    CellContext,
    ColumnContext,
    RowContext,
} from './direct-report-contribution-grid.model';
import {
    DirectReportScorecardDisplay,
    retrieveCellData,
    retrieveExpandedCellData,
} from './direct-report-scorecard-grid.model';
import { GridEpicBridge } from './grid.epic.bridge';
import { ScoredardBridgeService } from './sc-bridge.service';
import { ColumnType, ScorecardSelection } from './team-grid.model';
import { DirectReportScorecardGrid } from './team-grid.state';

@Component({
    selector: 'cmps-direct-report-scorecard-grid',
    templateUrl: './direct-report-scorecard-grid.component.html',
})
@TakeUntilDestroy()
export class DirectReportScorecardGridComponent
    implements OnChanges, OnInit, OnDestroy
{
    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ColumnType = ColumnType;
    /** @private */ _SortOrder = SortOrder;
    /** @private */ _prevModel: DirectReportScorecardGrid;

    @Input() mode: TemplateMode;
    @Input() model: DirectReportScorecardGrid;
    @Output() sortChange: EventEmitter<string /* columnId */>;
    @Output() expandColumns: EventEmitter<string /* columnId */>;
    @Output() onDRSCSelect: EventEmitter<ScorecardSelection>;
    @Output() onGridSelect: EventEmitter<GridType>;
    private _blackoutBindWidthResize: boolean;

    /** @private */ _rendererOpts: DivRendererOptions;
    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context<
            ColumnContext,
            RowContext,
            ColumnContext | CellContext
        >,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => e.type === 'row');
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        } else {
            return null;
        }
    };

    public headerColour: string;

    constructor(
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _scBridge: ScoredardBridgeService,
        private readonly _epicBridge: GridEpicBridge,
        @Inject(CMPS_SCORECARD_WIDGET) /** @private */
        public _widget: Widget<ScorecardGridOptions>,
    ) {
        this.sortChange = new EventEmitter();
        this.expandColumns = new EventEmitter();
        this.onDRSCSelect = new EventEmitter();
        this.onGridSelect = new EventEmitter();
        this.headerColour = _widget.options.directReportGrids.find(
            (g) => g.gridType === 'directReportsView',
        ).headerColour;
    }

    ngOnInit(): void {
        this._rendererOpts = {
            columns: this.model ? this.model.columns : null,
            defaultRenderFn: null,
            height: 350,
            horizontalScrollPadding: 9999,
            verticalScrollPadding: 9999,
        };
        this.onGridSelect.emit(GridType.Direct_Reports_View);

        // TODO revise. a problem with the pinned scorecard header +
        //  window scroll height causing the window scrollbar to flicker +
        //  while the window is resizing can cause edge-case weirdness
        bindWidthResize(this._el.nativeElement.parentElement.parentElement)
            .pipe(
                filter(() => !this._blackoutBindWidthResize),
                tap(() => this._onWillInflate()),
                debounceTime(250),
                tap(() => this._onDidInflate()),
                catchError(() => of(null)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onSortClick(column: Column<ColumnContext>): void {
        this.sortChange.emit(column.id);
    }

    layout(): void {
        this._onWillInflate();
        this._onDidInflate();
    }

    /** @private */ _onExpandClick(
        $event: Event,
        column: Column<ColumnContext>,
    ): void {
        $event.stopPropagation();
        this.expandColumns.emit(column.id);
        column.ctx.isColumnExpanded = !column.ctx.isColumnExpanded;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._grid.renderer.layout();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('model')) {
            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.columns !== this.model.columns)
            ) {
                addColumnsMeasure(this.model.columns);

                this._rendererOpts = {
                    ...this._rendererOpts,
                    columns: this.model.columns,
                };
            }

            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.rows !== this.model.rows)
            ) {
                addRowsMeasure(this.model.rows);

                if (
                    this._prevModel &&
                    this._prevModel.columns === this.model.columns
                ) {
                    this._onWillInflate();
                    this._onDidInflate();
                }
            }

            if (
                ((!this._prevModel && this.model) ||
                    (this._prevModel &&
                        this.model &&
                        this._prevModel.sortDefs !== this.model.sortDefs)) &&
                this._grid &&
                this._grid.renderer
            ) {
                this._grid.renderer.contentColumns.columns.forEach((r) =>
                    r.cells.forEach((l) => l.detectChanges({ onlySelf: true })),
                );
            }

            this._prevModel = this.model;
        }
    }

    ngOnDestroy(): void {
        this.sortChange.complete();
        this.expandColumns.complete();
        this.onDRSCSelect.complete();
        this.onGridSelect.complete();
    }

    /** @private */
    _onWillInflate(): void {
        // TODO revise. this is just a mitigation. the real problem is scorecardComponent.stateToProps()
        if (!this._scBridge.componentScorecard.bridgeIsExporting()) {
            this._el.nativeElement.style.visibility = 'hidden';
        }
        this._blackoutBindWidthResize = true;
    }

    /** @private */
    _onDidInflate(): void {
        this._changeDetectorRef.detectChanges();
        if (!this._grid || !this._grid.renderer) {
            return;
        }
        this._epicBridge.renderer_direct_reports = this._grid.renderer;

        // TODO explicit column filtering
        const leftColumns = [
            ColumnType.USER_NAME,
            ColumnType.SUPERGROUP_NAME,
            ColumnType.PERIOD,
        ].map(String);
        const columns =
            this.model.columns &&
            flattenArrayWithObj(this.model.columns, 'children');
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const left =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns && columns.filter((e) => leftColumns.includes(e.id));
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const right =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns &&
            columns.filter(
                (e) =>
                    e.id.includes('-') ||
                    e.id === ColumnType.OVERALL_SCORE.toString(),
            );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        autoSizeGrid(
            this._grid.renderer,
            left,
            right,
            this._el.nativeElement.parentElement.parentElement,
        ).then(() => {
            this._el.nativeElement.style.visibility = '';
            this._blackoutBindWidthResize = false;
        });
    }

    /** @private */ _retrieveCellValue(
        data: DirectReportScorecardDisplay[],
        metricType: string,
        metricId: string,
        item: string,
    ): string {
        return retrieveCellData(data, metricType, metricId, item);
    }

    /** @private */ _retrieveExpandedCellData(
        data: DirectReportScorecardDisplay[],
        metricType: string,
        metricId: string,
        item: string,
    ): string {
        return retrieveExpandedCellData(data, metricType, metricId, item);
    }

    /** @private */ _onDRScorecardSelect(
        item: DirectReportScorecardDisplay,
    ): void {
        const user = new User();
        user.id = item.userId;
        user.superGroup = item.superGroup;
        const names = item.directReport.split(' ');
        user.firstName = names[0];
        user.lastName = names[1];
        const scSelection: ScorecardSelection = {
            user,
            scorecardId: item.scorecardId,
        };
        this.onDRSCSelect.emit(scSelection);
    }
}
